import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/Button';

import { navigateTo } from '../router';

// Written by tools/site_check.py. The check runs from outside the deployment on
// purpose, so what this page shows is "the last time someone checked", never
// "right now" — the run timestamp is displayed prominently so staleness is
// visible instead of implied.
//
// Read from the database first. The static artifact was baked into the image at
// build time, which made this page structurally one deploy behind: a run against
// the newly-deployed site could not reach visitors until the *next* deploy, so
// the page showed the previous release's sha. Publishing to a table fixes that.
// The file is kept as a fallback so a deployment that has not published yet (or
// has STATUS_PUBLISH_TOKEN unset) still renders its last committed snapshot.
const REPORT_API_URL = '/api/status/latest';
const REPORT_FILE_URL = '/status-latest.json';

async function loadReport(): Promise<StatusReport> {
  const fromApi = await fetch(REPORT_API_URL, { cache: 'no-store' }).catch(() => null);
  if (fromApi?.ok) return (await fromApi.json()) as StatusReport;

  const fromFile = await fetch(REPORT_FILE_URL, { cache: 'no-store' }).catch(() => null);
  if (fromFile?.ok) return (await fromFile.json()) as StatusReport;

  // Report the API's status, not the file's: the file is the legacy path and a
  // 404 there is expected on a deployment that has moved to publishing.
  throw new Error(`no status report published (HTTP ${fromApi?.status ?? 'unreachable'})`);
}

type Verdict = 'ok' | 'slow' | 'fail';

interface Probe {
  group: string;
  label: string;
  url: string;
  status: number | null;
  total_ms: number | null;
  verdict: Verdict;
  note: string;
  ttfb_ms: number | null;
  bytes: number | null;
  budget_ms: number | null;
  samples: number[];
}

interface AgentRun {
  label: string;
  task: string;
  succeeded: boolean | null;
  steps: number;
  duration_s: number;
  finding: string;
  friction: string;
  errors: string[];
  urls: string[];
  contradicted: boolean;
  cross_check: string;
}

interface StatusReport {
  generated_at: string;
  base_url: string;
  git_sha: string | null;
  duration_s: number;
  summary: { verdict: Verdict; ok: number; slow: number; fail: number; agent_checks_run: boolean };
  probes: Probe[];
  agent_runs: AgentRun[];
}

const VERDICT_TEXT: Record<Verdict, string> = {
  ok: 'text-good',
  slow: 'text-warn',
  fail: 'text-del',
};
const VERDICT_BG: Record<Verdict, string> = { ok: 'bg-good', slow: 'bg-warn', fail: 'bg-del' };
const VERDICT_LABEL: Record<Verdict, string> = { ok: 'Healthy', slow: 'Slow', fail: 'Failing' };

const GROUP_LABELS: Record<string, string> = {
  page: 'Pages',
  asset: 'Assets',
  api: 'APIs',
  query: 'Retrieval',
};
const GROUP_ORDER = ['page', 'asset', 'api', 'query'];

function formatAge(iso: string): { text: string; stale: boolean } {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return { text: iso, stale: true };
  const minutes = Math.round((Date.now() - then) / 60_000);
  if (minutes < 1) return { text: 'just now', stale: false };
  if (minutes < 60) return { text: `${minutes} min ago`, stale: false };
  const hours = Math.round(minutes / 60);
  if (hours < 24) return { text: `${hours}h ago`, stale: hours >= 12 };
  const days = Math.round(hours / 24);
  return { text: `${days}d ago`, stale: true };
}

function formatMs(ms: number | null): string {
  if (ms === null) return '—';
  return ms >= 1000 ? `${(ms / 1000).toFixed(1)}s` : `${Math.round(ms)}ms`;
}

/** Bar is scaled against 2x budget — the point where a probe stops being "slow" and fails. */
function LatencyBar({ probe }: { probe: Probe }) {
  if (probe.total_ms === null || !probe.budget_ms) return null;
  const pct = Math.min(100, (probe.total_ms / (probe.budget_ms * 2)) * 100);
  return (
    <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-rule-soft">
      <div
        className={`h-full rounded-full ${VERDICT_BG[probe.verdict]}`}
        style={{ width: `${pct}%` }}
      />
    </div>
  );
}

function ProbeRow({ probe }: { probe: Probe }) {
  return (
    <div className="border-b border-rule-soft px-4 py-3 last:border-0" data-testid="status-probe">
      <div className="flex items-baseline justify-between gap-4">
        <div className="min-w-0">
          <span
            className={`mr-2 inline-block h-2 w-2 shrink-0 rounded-full ${VERDICT_BG[probe.verdict]}`}
          />
          <span className="text-sm font-medium text-ink">{probe.label}</span>
          {probe.status !== null && probe.status !== 200 && (
            <span className="ml-2 font-mono text-2xs text-del">HTTP {probe.status}</span>
          )}
        </div>
        <span className={`shrink-0 font-mono text-sm ${VERDICT_TEXT[probe.verdict]}`}>
          {formatMs(probe.total_ms)}
        </span>
      </div>
      <LatencyBar probe={probe} />
      <p className="mt-1.5 text-xs text-dim">
        {probe.note}
        {probe.budget_ms ? (
          <span className="text-3xs"> · budget {formatMs(probe.budget_ms)}</span>
        ) : null}
      </p>
    </div>
  );
}

function AgentCard({ run }: { run: AgentRun }) {
  const tone = run.contradicted ? 'text-warn' : run.succeeded ? 'text-good' : 'text-del';
  const label = run.contradicted ? 'contradicted' : run.succeeded ? 'succeeded' : 'failed';
  return (
    <div className="border border-rule p-4" data-testid="status-agent-run">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-medium text-ink">{run.label}</h3>
        <span className={`shrink-0 font-mono text-2xs uppercase tracking-wider ${tone}`}>
          {label}
        </span>
      </div>
      <p className="mt-1 font-mono text-3xs text-dim">
        {run.steps} steps · {run.duration_s.toFixed(1)}s
      </p>
      {run.contradicted && (
        <p className="mt-2 border-l-2 border-warn pl-3 text-xs text-warn">
          {run.cross_check}. Either the UI is broken or the agent misread it — reproduce with a
          scripted browser before acting on this.
        </p>
      )}
      {run.finding && (
        <p className="mt-2 font-serif text-sm leading-relaxed text-ink">{run.finding}</p>
      )}
      {run.friction && (
        <p className="mt-2 border-l-2 border-warn pl-3 font-serif text-sm leading-relaxed text-dim">
          {run.friction}
        </p>
      )}
      {run.errors.length > 0 && (
        <ul className="mt-2 space-y-0.5">
          {run.errors.map((err, i) => (
            <li key={i} className="font-mono text-3xs text-del">
              {err}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function StatusPage() {
  const [report, setReport] = useState<StatusReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    loadReport()
      .then((data: StatusReport) => {
        if (!cancelled) setReport(data);
      })
      .catch((err: Error) => {
        if (!cancelled) setError(err.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return <div className="py-16 text-center text-sm text-dim">Loading status report…</div>;
  }

  if (error || !report) {
    return (
      <div className="mx-auto max-w-xl py-16 text-center" data-testid="status-empty">
        <h2 className="text-lg font-semibold text-ink">No status report yet</h2>
        <p className="mt-2 text-sm text-dim">{error}</p>
        <p className="mt-4 text-sm text-dim">
          Run the site check with <code className="font-mono text-xs text-link">--publish</code> and
          it appears here immediately — no redeploy needed:
        </p>
        <pre className="mt-3 overflow-x-auto border border-rule bg-code-bg p-3 text-left font-mono text-xs text-ink">
          npm run status -- --publish
        </pre>
        <p className="mt-3 text-xs text-dim">
          Publishing requires <code className="font-mono text-link">STATUS_PUBLISH_TOKEN</code> set
          on the server and passed as <code className="font-mono text-link">--publish-token</code>.
        </p>
        <div className="mt-6">
          <Button size="sm" onClick={() => navigateTo('/')}>
            Back home
          </Button>
        </div>
      </div>
    );
  }

  const { summary } = report;
  const age = formatAge(report.generated_at);
  const grouped = GROUP_ORDER.map((group) => ({
    group,
    probes: report.probes.filter((p) => p.group === group),
  })).filter((entry) => entry.probes.length > 0);

  return (
    <div className="mx-auto max-w-3xl py-10" data-testid="lc-page-status">
      <header>
        <p className="font-mono text-2xs uppercase tracking-wider text-dim">
          Synthetic status check
        </p>
        <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <h1 className={`text-3xl font-semibold tracking-tight ${VERDICT_TEXT[summary.verdict]}`}>
            {VERDICT_LABEL[summary.verdict]}
          </h1>
          <span className="text-sm text-dim">
            {summary.ok} ok · {summary.slow} slow · {summary.fail} failing
          </span>
        </div>
        <p className="mt-2 text-sm text-dim">
          Checked <span className="font-mono text-ink">{report.base_url}</span>{' '}
          <span className={age.stale ? 'text-warn' : ''}>{age.text}</span>
          {report.git_sha && (
            <>
              {' '}
              at <span className="font-mono">{report.git_sha}</span>
            </>
          )}{' '}
          · took {report.duration_s}s
        </p>
        {age.stale && (
          <p className="mt-3 border-l-2 border-warn pl-3 text-xs text-dim">
            This report is a snapshot from the last run, not a live check — treat it as history, not
            current state.
          </p>
        )}
      </header>

      {grouped.map(({ group, probes }) => (
        <section key={group} className="mt-8">
          <h2 className="font-mono text-2xs uppercase tracking-wider text-dim">
            {GROUP_LABELS[group] ?? group}
          </h2>
          <div className="mt-2 border border-rule">
            {probes.map((probe) => (
              <ProbeRow key={`${probe.group}-${probe.label}`} probe={probe} />
            ))}
          </div>
        </section>
      ))}

      <section className="mt-8">
        <h2 className="font-mono text-2xs uppercase tracking-wider text-dim">Agent experience</h2>
        {report.agent_runs.length === 0 ? (
          <p className="mt-2 text-sm text-dim">
            Not run. HTTP probes prove pages were delivered, not that they render — pass{' '}
            <code className="rounded bg-bg px-1 py-0.5 font-mono text-[0.9em] text-link">
              --agent
            </code>{' '}
            to have a browser-use agent attempt real tasks on the site.
          </p>
        ) : (
          <>
            <p className="mt-2 text-xs text-dim">
              An LLM agent attempting real tasks. Findings are leads, not measurements — each one is
              cross-checked against the HTTP probes above, and agents have reported both false
              absences and false freezes while grading themselves as successful.
            </p>
            <div className="mt-3 space-y-3">
              {report.agent_runs.map((run) => (
                <AgentCard key={run.label} run={run} />
              ))}
            </div>
          </>
        )}
      </section>

      <p className="mt-10 border-t border-rule pt-4 text-xs text-dim">
        Measured from wherever the check ran, so network conditions are part of every number.
        Passive per-query latency is recorded separately in{' '}
        <span className="font-mono">query_logs</span>.
      </p>
    </div>
  );
}
